import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentPropsWithoutRef,
  type ComponentType,
  type ReactNode,
} from 'react';
import { report } from '../lib/errorNotices';

/**
 * The shell's popovers and sheets, loaded after first paint rather than with it.
 *
 * ── WHY ──────────────────────────────────────────────────────────────────────────────────────
 *
 * The account menu, the notifications bell, the phone's More sheet, the search palette and the
 * leave-without-saving warning are on every page, so everything they import statically is in the
 * entry chunk. What they import is Radix Popover and Dialog, and behind Popover all of floating-ui
 * — the largest block left on first paint (DESIGN_SYSTEM.md §8), for controls nobody can use until
 * the page is up and somebody reaches for one. So the TRIGGERS stay eager — they carry the unread
 * badge and whose books you are in, which have to be on screen at once — and what opens behind them
 * is one chunk, `overlays.ts`, fetched when the browser is idle.
 *
 * ── WHY NOT `React.lazy` ─────────────────────────────────────────────────────────────────────
 *
 * A lazy component whose import fails throws to the nearest error boundary, and for the shell that
 * is the root: a dropped connection while the chunk was in flight would replace the whole page
 * with an error screen over a menu. Held in a store instead, a failure leaves the placeholder
 * trigger where it was and a click on it says what happened. The toaster is deliberately NOT here —
 * see `ErrorToaster` — because an error display that has to download itself fails in exactly the
 * cases it exists for.
 *
 * ── A CLICK BEFORE THE CHUNK HAS LANDED ──────────────────────────────────────────────────────
 *
 * The placeholder is the same button, so it looks and measures identically (no layout shift on
 * the swap). Clicked early, it asks for the chunk now and the overlay mounts already open; a
 * keyboard user who had focused it keeps focus across the swap.
 */

type Overlays = typeof import('./overlays');

/**
 * Say that the chunk could not be fetched, when somebody reaches for something in it.
 *
 * RELOAD, NOT "TRY AGAIN". Chromium keeps a failed module import for the life of the page, so a
 * second `import()` of the same URL is answered from that record without going back to the network
 * — measured, not assumed. The retry below still happens, because browsers that follow the current
 * spec do fetch again, but the sentence cannot promise it. A deploy that replaced the chunk looks
 * the same as a dropped connection from here, and reloading is the answer to both.
 *
 * Reported directly rather than through `notifyError`, whose connection branch would say "try
 * again". One id, so every trigger clicked while it is broken updates one notice.
 */
export function reportOverlayFailure(title: string): void {
  report({
    id: 'overlays',
    title,
    detail:
      'Part of ProManage could not be downloaded. Check your connection, then reload the page.',
  });
}

let loaded: Overlays | null = null;
let pending: Promise<Overlays> | null = null;
const listeners = new Set<() => void>();

export function loadOverlays(): Promise<Overlays> {
  pending ??= import('./overlays').then(
    (module) => {
      loaded = module;
      for (const listener of listeners) listener();
      return module;
    },
    (error: unknown) => {
      // Forget the failure, so the next attempt asks again rather than replaying this rejection.
      pending = null;
      throw error;
    },
  );
  return pending;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

const getLoaded = () => loaded;

/**
 * The overlays module once it has arrived, `null` until then — and asks for it when the browser
 * is idle.
 *
 * Idle, not immediately: this is off the critical path by design, so it queues behind the first
 * screen's own route chunk and data. `setTimeout` for Safari, which still has no
 * `requestIdleCallback`, at two seconds for the same reason — the arrangement `UniversalSearch`
 * uses to warm its panel. Every caller schedules; `loadOverlays` is idempotent, so they share one
 * request.
 */
export function useOverlays(): Overlays | null {
  const overlays = useSyncExternalStore(subscribe, getLoaded, getLoaded);

  useEffect(() => {
    if (loaded) return;
    const warm = () => {
      // Quiet here: nobody asked for anything yet. A click retries, and says so if it fails.
      loadOverlays().catch(() => undefined);
    };
    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(warm, { timeout: 5_000 });
      return () => cancelIdleCallback(handle);
    }
    const timer = setTimeout(warm, 2_000);
    return () => clearTimeout(timer);
  }, []);

  return overlays;
}

/** The overlays that open from a trigger — the ones `DeferredOverlay` can draw. */
type TriggeredOverlay = {
  [Name in keyof Overlays]: Overlays[Name] extends ComponentType<DeferredOverlayProps>
    ? Name
    : never;
}[keyof Overlays];

/** What every deferred popover takes: the trigger's own props and contents, drawn by it. */
export interface DeferredOverlayProps {
  triggerProps: ComponentPropsWithoutRef<'button'>;
  children: ReactNode;
  /** Somebody clicked the placeholder before the chunk landed — mount open. */
  defaultOpen: boolean;
  /** The placeholder had keyboard focus when it was swapped out — take it back. */
  focusTrigger: boolean;
}

/**
 * For the real trigger: a ref that takes focus on mount when the placeholder it replaced had it.
 *
 * The swap is a remount — the button's parent changes from nothing to a Radix root — so without
 * this a keyboard user who had tabbed to the account button in the first second would find focus
 * dropped back to the page.
 */
export function useRefocus(focusTrigger: boolean) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusTrigger) ref.current?.focus();
    // On mount only: this is about the swap, not about any later render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

/**
 * A trigger whose overlay arrives later.
 *
 * `overlay` names the component in `overlays.ts`, and only one that takes a trigger will
 * type-check. The trigger's props and children are passed through to it unchanged, so the
 * placeholder and the real trigger cannot drift apart.
 */
export function DeferredOverlay({
  overlay,
  triggerProps,
  children,
  failureTitle,
}: {
  overlay: TriggeredOverlay;
  triggerProps: ComponentPropsWithoutRef<'button'>;
  children: ReactNode;
  failureTitle: string;
}) {
  const overlays = useOverlays();
  const [requested, setRequested] = useState(false);
  const [focused, setFocused] = useState(false);

  if (overlays) {
    const Overlay = overlays[overlay];
    return (
      <Overlay
        triggerProps={triggerProps}
        defaultOpen={requested}
        // An overlay mounting open moves focus into itself, and returns it to the trigger on close.
        focusTrigger={focused && !requested}
      >
        {children}
      </Overlay>
    );
  }

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={false}
      data-state="closed"
      {...triggerProps}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onClick={() => {
        setRequested(true);
        loadOverlays().catch(() => {
          setRequested(false);
          reportOverlayFailure(failureTitle);
        });
      }}
    >
      {children}
    </button>
  );
}
